import { useEditor, EditorContent } from '@tiptap/react'
import { useEffect } from 'react'
import StarterKit from '@tiptap/starter-kit'
import OrderedList from '@tiptap/extension-ordered-list'
import BulletList from '@tiptap/extension-bullet-list'
import ListItem from '@tiptap/extension-list-item'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { TextAlign } from '@tiptap/extension-text-align'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Plus,
  Minus,
  Trash2,
  Palette,
  ChevronDown
} from 'lucide-react'

// CSS pour ProseMirror
import 'prosemirror-view/style/prosemirror.css'

export default function TipTapEditor({ content, onChange, placeholder = "Saisissez votre contenu..." }) {
  // Extensions custom pour les styles de listes via list-style-type
  const OrderedListExtended = OrderedList.extend({
    addAttributes() {
      return {
        listStyleType: {
          default: 'decimal',
          parseHTML: element => element.style.listStyleType || null,
          renderHTML: attributes => ({ style: `list-style-type: ${attributes.listStyleType}` })
        }
      }
    }
  })

  const BulletListExtended = BulletList.extend({
    addAttributes() {
      return {
        listStyleType: {
          default: 'disc',
          parseHTML: element => element.style.listStyleType || null,
          renderHTML: attributes => ({ style: `list-style-type: ${attributes.listStyleType}` })
        }
      }
    }
  })

  const editor = useEditor({
    extensions: [
      // Noyau
      StarterKit.configure({
        heading: true,
        orderedList: false,
        bulletList: false,
        listItem: false,
        underline: false,
      }),
      // Listes avec attributs personnalisés
      ListItem,
      OrderedListExtended,
      BulletListExtended,
      // Styles de texte et couleurs
      TextStyle,
      Color,
      // Soulignement
      Underline,
      // Alignements
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'tiptap-table w-full border border-gray-200 rounded-lg text-sm'
        }
      }),
      TableRow,
      TableHeader,
      TableCell
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML())
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-4',
        placeholder: placeholder,
      },
    },
    // Forcer la mise à jour du contenu initial
    immediatelyRender: false,
  })

  // Mettre à jour l'éditeur quand le contenu change
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML()
      // Normaliser les deux contenus pour la comparaison (supprimer les espaces et les retours à la ligne)
      const normalizedCurrent = currentContent.trim().replace(/\s+/g, ' ')
      const normalizedNew = (content || '').trim().replace(/\s+/g, ' ')
      
      // Mettre à jour seulement si le contenu est différent
      if (normalizedCurrent !== normalizedNew) {
        editor.commands.setContent(content || '')
      }
    }
  }, [editor, content])

  const tableButtonClass = "px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 transition-colors flex items-center gap-1"

  if (!editor) {
    return (
      <div className="border border-gray-300 rounded-lg p-4 min-h-[200px] flex items-center justify-center">
        <div className="text-gray-500">Chargement de l'éditeur...</div>
      </div>
    )
  }

  const addLink = () => {
    const url = window.prompt('URL du lien:')
    if (url) {
      // Pour l'instant, on ajoute juste le texte du lien
      editor.chain().focus().insertContent(`<a href="${url}" target="_blank">${url}</a>`).run()
    }
  }

  const addImage = () => {
    const url = window.prompt('URL de l\'image:')
    if (url) {
      // Pour l'instant, on ajoute juste le texte de l'image
      editor.chain().focus().insertContent(`<img src="${url}" alt="Image" style="max-width: 100%; height: auto;" />`).run()
    }
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-50 border-b border-gray-300">
        {/* Formatage de texte */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
          title="Gras"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
          title="Italique"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-300' : ''}`}
          title="Souligner"
        >
          <UnderlineIcon size={16} />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Titres */}
        <select
          onChange={(e) => {
            const level = Number(e.target.value)
            if (level === 0) {
              editor.chain().focus().setParagraph().run()
            } else {
              editor.chain().focus().setHeading({ level }).run()
            }
          }}
          className="p-2 rounded border border-gray-300 bg-white text-sm"
          title="Titres"
          value={
            [1, 2, 3, 4, 5, 6].find(level => editor.isActive('heading', { level })) ?? 0
          }
        >
          <option value={0}>Paragraphe</option>
          <option value={1}>Titre 1</option>
          <option value={2}>Titre 2</option>
          <option value={3}>Titre 3</option>
          <option value={4}>Titre 4</option>
          <option value={5}>Titre 5</option>
          <option value={6}>Titre 6</option>
        </select>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Listes */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
          title="Liste à puces"
        >
          <List size={16} />
        </button>
        {/* Sélecteur compact sans contour (puces) */}
        <div className="relative inline-block align-middle w-5 h-5" title="Style puces">
          <ChevronDown size={14} className="text-gray-600" />
          <select
            onChange={(e) => {
              const style = e.target.value
              if (!style) return
              const chain = editor.chain().focus()
              if (!editor.isActive('bulletList')) chain.toggleBulletList()
              chain.updateAttributes('bulletList', { listStyleType: style }).run()
              e.target.selectedIndex = 0
            }}
            className="absolute inset-0 opacity-0 cursor-pointer"
            defaultValue=""
          >
            <option value="" disabled></option>
            <option value="circle">○ Cercle</option>
            <option value="square">■ Carré</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
          title="Liste numérotée"
        >
          <ListOrdered size={16} />
        </button>
        {/* Sélecteur compact sans contour (numérotation) */}
        <div className="relative inline-block align-middle w-5 h-5" title="Style numérotation">
          <ChevronDown size={14} className="text-gray-600" />
          <select
            onChange={(e) => {
              const style = e.target.value
              if (!style) return
              const chain = editor.chain().focus()
              if (!editor.isActive('orderedList')) chain.toggleOrderedList()
              chain.updateAttributes('orderedList', { listStyleType: style }).run()
              e.target.selectedIndex = 0
            }}
            className="absolute inset-0 opacity-0 cursor-pointer"
            defaultValue=""
          >
            <option value="" disabled></option>
            <option value="upper-roman">I, II, III</option>
            <option value="lower-roman">i, ii, iii</option>
            <option value="upper-alpha">A, B, C</option>
            <option value="lower-alpha">a, b, c</option>
          </select>
        </div>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Alignements */}
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-300' : ''}`}
          title="Aligner à gauche"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-300' : ''}`}
          title="Centrer"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-300' : ''}`}
          title="Aligner à droite"
        >
          <AlignRight size={16} />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Couleur de texte */}
        <label className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-200 cursor-pointer" title="Couleur du texte">
          <Palette size={16} />
          <input
            type="color"
            className="w-6 h-6 border-0 p-0 bg-transparent cursor-pointer"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </label>
        <button
          type="button"
          onClick={() => editor.chain().focus().unsetColor().run()}
          className="p-2 rounded hover:bg-gray-200"
          title="Réinitialiser la couleur"
        >
          Réinit. couleur
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Liens et images */}
        <button
          type="button"
          onClick={addLink}
          className="p-2 rounded hover:bg-gray-200"
          title="Ajouter un lien"
        >
          <LinkIcon size={16} />
        </button>
        <button
          type="button"
          onClick={addImage}
          className="p-2 rounded hover:bg-gray-200"
          title="Ajouter une image"
        >
          <ImageIcon size={16} />
        </button>

        <div className="flex items-center gap-1 ml-2">
          <button
            type="button"
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            className={tableButtonClass}
            title="Insérer un tableau 3x3"
          >
            <TableIcon size={14} />
            Tableau
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            className={tableButtonClass}
            title="Ajouter une ligne après"
          >
            <Plus size={12} />
            Ligne
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            className={tableButtonClass}
            title="Ajouter une colonne après"
          >
            <Plus size={12} />
            Colonne
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteRow().run()}
            className={tableButtonClass}
            title="Supprimer la ligne"
          >
            <Minus size={12} />
            Ligne
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            className={tableButtonClass}
            title="Supprimer la colonne"
          >
            <Minus size={12} />
            Colonne
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().deleteTable().run()}
            className={`${tableButtonClass} text-red-600 border-red-200`}
            title="Supprimer le tableau"
          >
            <Trash2 size={12} />
            Tableau
          </button>
        </div>
      </div>

      {/* Éditeur */}
      <div className="tiptap-editor">
        <EditorContent 
          editor={editor} 
          className="min-h-[200px]"
          placeholder={placeholder}
        />
      </div>
    </div>
  )
}
